import {
  Box,
  Checkbox,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { useTranslation } from "react-i18next";
import type { TableHeadCell } from "../../../../shared/models/TableModels";
import type { Order } from "../../../../shared/models/TableModels";
import { TableLoading } from "../../../../shared/components/skeleton/table-loading/TableLoading";
import type { DocumentRowUi, RoleOptionUi } from "../../types/documentManagementUiTypes";
import { uploadStatusChipColor, uploadStatusChipLabel } from "../../utils/documentLibraryDisplayUtils";
import type { LibraryMediaFilter } from "../../utils/documentLibraryMediaUtils";
import { DocumentLibraryRoleCell } from "./DocumentLibraryRoleCell";
import { DocumentLibraryRowActions } from "./DocumentLibraryRowActions";
import { LibraryFileTypeIcon } from "./LibraryFileTypeIcon";
import { ContentTypeSegmentedControl } from "../shared/ContentTypeSegmentedControl";

export type DocumentLibraryTableProps = {
  libraryMediaFilter: LibraryMediaFilter;
  onLibraryMediaFilterChange: (value: LibraryMediaFilter) => void;
  documentCount: number;
  videoCount: number;
  visibleDocuments: DocumentRowUi[];
  totalFilteredCount: number;
  loadingDocuments: boolean;
  hasNameFilter: boolean;
  tablePage: number;
  rowsPerPage: number;
  order: Order;
  orderBy: string;
  headers: TableHeadCell[];
  selectedCount: number;
  allVisibleSelected: boolean;
  someVisibleSelected: boolean;
  bulkDeleteBusy: boolean;
  selectedIdSet: Set<number>;
  onToggleSelectAll: () => void;
  onToggleRow: (documentId: number) => void;
  onSort: (property: string) => void;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => void;
  onRowsPerPageChange: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  rowActionsDisabled: (documentId: number) => boolean;
  onViewEdit: (row: DocumentRowUi) => void;
  onDelete: (row: DocumentRowUi) => void;
  onRetryProcessing?: (row: DocumentRowUi) => void;
  onToggleActive: (row: DocumentRowUi) => void;
  roleOptions: RoleOptionUi[];
  rolesBusyId: number | null;
  onSaveDocumentRoles: (
    row: DocumentRowUi,
    roleAccess: string[]
  ) => Promise<boolean>;
};

export function DocumentLibraryTable({
  libraryMediaFilter,
  onLibraryMediaFilterChange,
  documentCount,
  videoCount,
  visibleDocuments,
  totalFilteredCount,
  loadingDocuments,
  hasNameFilter,
  tablePage,
  rowsPerPage,
  order,
  orderBy,
  headers,
  selectedCount,
  allVisibleSelected,
  someVisibleSelected,
  bulkDeleteBusy,
  selectedIdSet,
  onToggleSelectAll,
  onToggleRow,
  onSort,
  onPageChange,
  onRowsPerPageChange,
  rowActionsDisabled,
  onViewEdit,
  onDelete,
  onRetryProcessing,
  onToggleActive,
  roleOptions,
  rolesBusyId,
  onSaveDocumentRoles,
}: DocumentLibraryTableProps) {
  const { t } = useTranslation();
  const isVideoList = libraryMediaFilter === "video";

  const colSpan = headers.length + 1;

  return (
    <Box component="section" className="dm-card dm-library-card">
      <Box
        className="dm-library-media-toolbar"
        sx={{
          display: "flex",
          flexWrap: "nowrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          mb: 1.5,
          minHeight: 40,
        }}
      >
        <ContentTypeSegmentedControl
          value={libraryMediaFilter}
          documentCount={documentCount}
          videoCount={videoCount}
          onChange={onLibraryMediaFilterChange}
          aria-label={t("opva.dmLibraryMediaTabsAria", {
            defaultValue: "Library file type",
          })}
        />
        <Typography
          variant="bodySmall"
          fontWeight={600}
          color="text.secondary"
          sx={{
            flexShrink: 0,
            minWidth: 140,
            textAlign: "right",
            whiteSpace: "nowrap",
          }}
        >
          {totalFilteredCount}{" "}
          {isVideoList
            ? t("opva.dmVideoCountLabel", { defaultValue: "videos" })
            : t("opva.dmDocumentCountLabel", {
                defaultValue: "documents",
              })}
          {selectedCount > 0
            ? ` · ${t("opva.dmBulkSelected", {
                defaultValue: "{{count}} selected",
                count: selectedCount,
              })}`
            : ""}
        </Typography>
      </Box>

      <TableContainer className="dm-table-wrap table-container">
        <Table
          stickyHeader
          size="medium"
          className="dm-table"
          aria-label={
            isVideoList
              ? t("opva.dmVideosTableAria", {
                  defaultValue: "Video library table",
                })
              : t("opva.dmDocumentsTableAria", {
                  defaultValue: "Document library table",
                })
          }
        >
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" className="dm-col-checkbox">
                <Checkbox
                  checked={allVisibleSelected}
                  indeterminate={someVisibleSelected && !allVisibleSelected}
                  disabled={
                    bulkDeleteBusy ||
                    loadingDocuments ||
                    visibleDocuments.length === 0
                  }
                  onChange={onToggleSelectAll}
                  inputProps={{
                    "aria-label": isVideoList
                      ? t("opva.dmSelectAllVideos", {
                          defaultValue: "Select all visible videos",
                        })
                      : t("opva.dmSelectAllDocuments", {
                          defaultValue: "Select all visible documents",
                        }),
                  }}
                />
              </TableCell>
              {headers.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  sortDirection={
                    orderBy === headCell.sortingProperty ? order : false
                  }
                  align={headCell.position}
                  className={
                    headCell.id === "actions"
                      ? "dm-col-actions"
                      : `dm-col-${headCell.id.replace(/_/g, "-")}`
                  }
                  sx={{ width: headCell.width ? `${headCell.width}%` : undefined }}
                >
                  {headCell.sorting ? (
                    <TableSortLabel
                      active={orderBy === headCell.sortingProperty}
                      direction={
                        orderBy === headCell.sortingProperty ? order : "asc"
                      }
                      onClick={() => onSort(headCell.sortingProperty)}
                    >
                      {headCell.label}
                      {orderBy === headCell.sortingProperty ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  ) : (
                    headCell.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {loadingDocuments ? (
            <TableLoading column={colSpan} row={8} />
          ) : (
            <TableBody>
              {visibleDocuments.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={colSpan}
                    align="center"
                    className="dm-table-empty"
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      {hasNameFilter
                        ? isVideoList
                          ? t("opva.dmNoVideosMatchSearch", {
                              defaultValue: "No videos match this search.",
                            })
                          : t("opva.searchDocumentNotAvailable", {
                              defaultValue:
                                "No documents match this search.",
                            })
                        : isVideoList
                          ? t("opva.dmNoVideos", {
                              defaultValue: "No videos in the library yet.",
                            })
                          : t("opva.dmNoDocuments", {
                              defaultValue:
                                "No documents in the library yet.",
                            })}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                visibleDocuments.map((row) => (
                  <TableRow
                    key={row.id}
                    className={
                      selectedIdSet.has(row.id) ? "dm-row--selected" : undefined
                    }
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIdSet.has(row.id)}
                        disabled={rowActionsDisabled(row.id)}
                        onChange={() => onToggleRow(row.id)}
                        inputProps={{
                          "aria-label": t("opva.dmSelectDocument", {
                            defaultValue: "Select document",
                          }),
                        }}
                      />
                    </TableCell>
                    <TableCell className="dm-col-original-file-name">
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ minWidth: 0, maxWidth: "100%" }}
                      >
                        <LibraryFileTypeIcon row={row} />
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          title={row.original_file_name}
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            minWidth: 0,
                            flex: 1,
                          }}
                        >
                          {row.original_file_name}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell className="dm-col-dataset-name">
                      <Typography
                        variant="body2"
                        title={row.dataset_name || undefined}
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.dataset_name || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell className="dm-col-company-name">
                      <Typography
                        variant="body2"
                        title={row.company_name || undefined}
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.company_name || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell className="dm-col-role-access">
                      <DocumentLibraryRoleCell
                        row={row}
                        roleOptions={roleOptions}
                        disabled={rowActionsDisabled(row.id)}
                        saving={rolesBusyId === row.id}
                        onSave={onSaveDocumentRoles}
                      />
                    </TableCell>
                    <TableCell className="dm-col-upload-status">
                      <Chip
                        size="small"
                        label={uploadStatusChipLabel(
                          row.upload_status,
                          row.lifecycle_status
                        )}
                        color={uploadStatusChipColor(
                          row.upload_status,
                          row.lifecycle_status
                        )}
                        variant="outlined"
                        title={row.processing_error || undefined}
                      />
                    </TableCell>
                    <TableCell className="dm-col-is-active">
                      <Chip
                        size="small"
                        clickable
                        disabled={rowActionsDisabled(row.id)}
                        className={
                          row.is_active
                            ? "dm-status-chip dm-status-chip--active"
                            : "dm-status-chip dm-status-chip--inactive"
                        }
                        label={
                          row.is_active
                            ? t("opva.dmActiveLabel", { defaultValue: "Active" })
                            : t("opva.dmDisabledLabel", {
                                defaultValue: "Inactive",
                              })
                        }
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!rowActionsDisabled(row.id)) {
                            onToggleActive(row);
                          }
                        }}
                        aria-label={
                          row.is_active
                            ? t("opva.dmSetInactive", {
                                defaultValue: "Set document inactive",
                              })
                            : t("opva.dmSetActive", {
                                defaultValue: "Set document active",
                              })
                        }
                      />
                    </TableCell>
                    <TableCell className="dm-col-actions" align="center">
                      <DocumentLibraryRowActions
                        row={row}
                        disabled={rowActionsDisabled(row.id)}
                        onViewEdit={onViewEdit}
                        onDelete={onDelete}
                        onRetryProcessing={onRetryProcessing}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          )}
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        className="pagination-container dm-library-pagination"
        rowsPerPageOptions={[10, 25, 50, 100]}
        count={totalFilteredCount}
        page={
          !totalFilteredCount || totalFilteredCount <= 0 ? 0 : tablePage
        }
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </Box>
  );
}
